import type { MainRouteShellPorts, ShellPorts, ShellPageElement } from '@src/core/bootstrap/ShellPorts.ts';
import { pickLoginShellPorts, pickShellCorePorts, shellPortsForPage } from '@src/core/bootstrap/ShellPorts.ts';
import { SHELL_PAGE_ACTIVATED_EVENT, SHELL_PORTS_UPDATED_EVENT } from '@core/domain/sessionEvents.ts';
import { DomShellLayoutAdapter, type ShellLayoutPort } from '../../driven/layout/DomShellLayoutAdapter.ts';
import {
  focusMainPageHeading,
  pageEnterClassForDirection,
} from '@src/modules/shell-routing/infrastructure/shellNavMotion.ts';
import { ensureShellPageComponentLoaded } from '../../shellLazyPageLoad.ts';
import { getShellMainWebContentTag } from '../../shellMainPageRegistry.ts';

function runWithViewTransition(updateDom: () => void, onDone?: () => void): void {
  if (typeof document.startViewTransition !== 'function') {
    updateDom();
    onDone?.();
    return;
  }
  try {
    const transition = document.startViewTransition(updateDom);
    // Safari/WebKit: cuando la transición se aborta (nav rápida, pestaña oculta) rechaza
    // `ready`/`updateCallbackDone` sin marcarlas manejadas → unhandledrejection → toast
    // "error inesperado" en cada cambio de página. Chrome las marca manejadas por spec.
    transition.ready?.catch(() => {});
    transition.updateCallbackDone?.catch(() => {});
    transition.finished.then(() => onDone?.()).catch(() => onDone?.());
  } catch {
    updateDom();
    onDone?.();
  }
}

/** Notifies mounted page components to reload (new mount and reuse). */
function notifyShellPageActivated(): void {
  globalThis.dispatchEvent(new CustomEvent(SHELL_PAGE_ACTIVATED_EVENT));
}

/** Mounts the custom element in `#main`, assigning `shellPorts` before lifecycle (no `innerHTML`). */
export function shellPageHandler(
  customElementTag: string,
  shellPorts: MainRouteShellPorts,
): () => Promise<void> {
  return async () => {
    try {
      const main = await shellPorts.layout.getMainContentHost();
      if (!main) return;
      if (main.firstElementChild?.tagName.toLowerCase() === customElementTag.toLowerCase()) {
        const el = main.firstElementChild as ShellPageElement;
        el.shellPorts = shellPorts;
        notifyShellPageActivated();
        return;
      }
      runWithViewTransition(
        () => {
          main.replaceChildren();
          const el = document.createElement(customElementTag) as ShellPageElement;
          el.classList.add(pageEnterClassForDirection());
          el.shellPorts = shellPorts;
          main.append(el);
          focusMainPageHeading(main);
        },
        () => notifyShellPageActivated(),
      );
    } catch (e) {
      shellPorts.telemetry.logError(e instanceof Error ? e : String(e), { source: 'shell.route.mount', customElementTag });
      shellPorts.notifications.error('No se pudo mostrar el contenido.');
    }
  };
}

/** Mounts Lit login in `#app` with injected ports (no `innerHTML` on the host). */
export function mountLoginInApp(shellPorts: ShellPorts): void {
  const ports = pickLoginShellPorts(shellPorts);
  const app = document.getElementById('app');
  if (!app) return;
  if (app.querySelector('login-component')) {
    const el = app.querySelector('login-component') as ShellPageElement;
    el.shellPorts = ports;
    return;
  }
  app.replaceChildren();
  const el = document.createElement('login-component') as ShellPageElement;
  el.shellPorts = ports;
  app.append(el);
}

/** Mounts Lit layout in `#app` with injected ports. Resolves when layout is in the DOM. */
export function mountLayoutRootInApp(shellPorts: ShellPorts): Promise<void> {
  const ports = pickShellCorePorts(shellPorts);
  const app = document.getElementById('app');
  if (!app) return Promise.resolve();
  if (app.querySelector('layout-component')) {
    const el = app.querySelector('layout-component') as ShellPageElement;
    el.shellPorts = ports;
    el.dispatchEvent(new CustomEvent(SHELL_PORTS_UPDATED_EVENT));
    return Promise.resolve();
  }
  app.replaceChildren();
  const el = document.createElement('layout-component') as ShellPageElement;
  el.classList.add('shell-app-root');
  el.shellPorts = ports;
  app.append(el);
  return Promise.resolve();
}

/** Default Lit shell: DOM layout + pages as custom elements. */
export class WebComponentsPresentationShellBase {
  constructor(private readonly layout: ShellLayoutPort) {}

  getLayoutPort(): ShellLayoutPort {
    return this.layout;
  }

  createMainPageHandler(pageId: string, shellPorts: ShellPorts): () => Promise<void> {
    const tag = getShellMainWebContentTag(pageId);
    const ports = shellPortsForPage(pageId, shellPorts);
    return async () => {
      await ensureShellPageComponentLoaded(pageId);
      await shellPageHandler(tag, ports)();
    };
  }

  mountLoginInApp(shellPorts: ShellPorts): void {
    mountLoginInApp(shellPorts);
  }

  mountLayoutRootInApp(shellPorts: ShellPorts): Promise<void> {
    return mountLayoutRootInApp(shellPorts);
  }
}

export function createWebComponentsPresentationShell(): WebComponentsPresentationShellBase {
  return new WebComponentsPresentationShellBase(new DomShellLayoutAdapter());
}
